import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { useNavigate } from 'react-router-dom';
import PagePreloader from './PagePreloader';

const PageTransition = ({ children }) => {
    const transitionRef = useRef(null);
    const navigate = useNavigate();
    const [preloadDestination, setPreloadDestination] = useState(null);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const handlePreloadCompleteRef = useRef(null);
    const isInitialMount = useRef(true);



    useEffect(() => {
        // Create the grid overlay
        const createGrid = () => {
            const grid = document.createElement('div');
            grid.className = 'load-grid';
            grid.style.cssText = `
                position: fixed;
                top: 0;
                left: 0;
                width: 100vw;
                height: 100vh;
                z-index: 9999;
                display: none;
                grid-template-columns: repeat(24, 1fr);
                grid-template-rows: repeat(16, 1fr);
                gap: 0;
            `;

            // Create grid items
            for (let i = 0; i < 384; i++) { // 24 columns * 16 rows = 384 cells
                const item = document.createElement('div');
                item.className = 'load-grid-item';
                item.style.cssText = `
                    width: 100%;
                    height: 100%;
                    background-color: #000;
                    opacity: 0;
                `;
                grid.appendChild(item);
            }

            document.body.appendChild(grid);
            return grid;
        };

        const grid = createGrid();

        // Handle navigation events from React Router
        const handleNavigationEvent = (e) => {
            const { path } = e.detail;
            startTransition(path);
        };

        const startTransition = (destination) => {
            setIsTransitioning(true);
            
            // Mark that we're in a transition
            sessionStorage.setItem('isTransitioning', 'true');
            
            // Show the grid and make it visible
            gsap.set(grid, { 
                display: 'grid',
                visibility: 'visible',
                pointerEvents: 'none'
            });

            // Animate grid items in
            gsap.fromTo('.load-grid-item', 
                { opacity: 0 },
                {
                    opacity: 1,
                    duration: 0.001,
                    stagger: {
                        amount: 0.3,
                        from: 'random'
                    },
                    onComplete: () => {
                        // Navigate to new page using React Router
                        navigate(destination);
                    }
                }
            );
        };

        const handlePreloadComplete = () => {
            // Start the transition after preloading is complete
            if (preloadDestination) {
                startTransition(preloadDestination);
                setPreloadDestination(null);
            }
        };

        // Store the function in ref so it can be accessed outside useEffect
        handlePreloadCompleteRef.current = handlePreloadComplete;

        // Handle navigation clicks
        const handleLinkClick = (e) => {
            const link = e.target.closest('a, button');
            if (!link) return;

            // Check if it's a navigation link (not external, not anchor, not new tab)
            const href = link.getAttribute('href');
            const target = link.getAttribute('target');
            
            // Skip if external link, anchor link, or opens in new tab
            if (target === '_blank' || 
                (href && href.startsWith('#')) ||
                (href && href.startsWith('http') && !href.includes(window.location.hostname))) {
                return;
            }

            // For React Router navigation buttons
            if (link.tagName === 'BUTTON' && link.classList.contains('nav-link')) {
                // Skip CONNECT button (it should just open email)
                if (link.classList.contains('connect-link')) {
                    return;
                }
                
                e.preventDefault();
                
                // Get the destination from the button's onClick handler or data attribute
                const destination = link.getAttribute('data-destination') || '/work';
                
                // Start preloading instead of immediate transition
                setPreloadDestination(destination);
                return;
            }

            // For regular links
            if (link.tagName === 'A' && href && !href.startsWith('http')) {
                e.preventDefault();
                startTransition(href);
            }
        };



        // Handle incoming transition (when page loads)
        const handleIncomingTransition = () => {
            // Show grid immediately and make it visible
            gsap.set(grid, { 
                display: 'grid',
                visibility: 'visible',
                pointerEvents: 'none'
            });
            gsap.set('.load-grid-item', { opacity: 1 });

            // Small delay to ensure page content is loaded
            setTimeout(() => {
                // Animate grid items out
                gsap.to('.load-grid-item', {
                    opacity: 0,
                    duration: 0.001,
                    stagger: {
                        amount: 0.3,
                        from: 'random'
                    },
                    onComplete: () => {
                        // Hide grid and ensure it's completely hidden
                        gsap.set(grid, { 
                            display: 'none',
                            visibility: 'hidden',
                            pointerEvents: 'none'
                        });
                    }
                });
            }, 100);
        };

        // Add event listeners
        document.addEventListener('click', handleLinkClick);
        window.addEventListener('navigation', handleNavigationEvent);

        // Check if we're coming from a transition
        const isComingFromTransition = sessionStorage.getItem('isTransitioning') === 'true';
        
        if (isComingFromTransition) {
            // We're coming from a transition, so show the grid and animate out
            sessionStorage.removeItem('isTransitioning');
            gsap.set(grid, { 
                display: 'grid',
                visibility: 'visible',
                pointerEvents: 'none'
            });
            gsap.set('.load-grid-item', { opacity: 1 });
            
            // Small delay to ensure page content is loaded
            setTimeout(() => {
                handleIncomingTransition();
            }, 100);
        } else if (isInitialMount.current) {
            // Initial page load
            setTimeout(handleIncomingTransition, 100);
            isInitialMount.current = false;
        }

        // Cleanup
        return () => {
            document.removeEventListener('click', handleLinkClick);
            window.removeEventListener('navigation', handleNavigationEvent);
            if (grid && grid.parentNode) {
                grid.parentNode.removeChild(grid);
            }
        };
    }, [navigate, preloadDestination]);

    return (
        <div ref={transitionRef}>
            <PagePreloader 
                destination={preloadDestination}
                onPreloadComplete={handlePreloadCompleteRef.current}
            >
                {children}
            </PagePreloader>
        </div>
    );
};

export default PageTransition; 